/* eslint-disable @typescript-eslint/restrict-plus-operands */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useRef, useState, VFC } from 'react';
import { Button, Col, Form, InputNumber, message, Row, Select, Spin, Switch, Table, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { SearchOutlined, ShopOutlined } from '@ant-design/icons';
import { apiClient, PalettCount, TruckOutlined, useAuth } from '@isocel/front-common';
import debounce from 'lodash/debounce';

import { BaseModel, Store, StoreUser } from '../../queries/api/types';
import { usePackagingConfig } from '../../queries/package';
import { usePalettes, useSupplierByOrganization } from '../../queries/palette';
import { useStores } from '../../queries/store';
import { useSupplierByStore } from '../../queries/api/supplier';
import { useWarehouses } from '../../queries/warehouse';
import { createStoreInventory, isStoreInventoryEnabled, useInventoryConfig } from '../../queries/api/store-inventory';
import './counter.less';

const { Option } = Select;

interface TableRow {
    key: string;
    entityId: string;
    entity: string;
    category: string;
    type: string;
    code: string;
    model: string;
    quantity: number;
}

type CounterType = 'store' | 'debt' | 'parc' | 'warehouse' | null;

interface DashboardRow {
    counter: string;
    entity: string;
    entityId: string;
    category: string;
    type: string;
    code: string;
    model: string;
    quantity: number;
    recievedCount?: number;
    returnedCount?: number;
}

type DashboardApiRow = Partial<DashboardRow> & {
    count?: number | string;
    package?: {
        _id?: string;
        id?: string;
        type?: { name?: string };
        model?: { name?: string };
        category?: string | { name?: string };
        orgInternCodes?: Array<{ code?: string }>;
    };
    store?: { name?: string };
};

const toSafeNumber = (value: unknown): number => {
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeDashboardRow = (row: DashboardApiRow): DashboardRow => ({
    counter: row.counter ?? '',
    entity: row.entity ?? row.store?.name ?? '',
    entityId: row.entityId ?? row.package?._id ?? row.package?.id ?? '',
    category: row.category ?? (typeof row.package?.category === 'object' ? row.package?.category?.name || '' : ''),
    type: row.type ?? row.package?.type?.name ?? '',
    code: row.code ?? row.package?.orgInternCodes?.[0]?.code ?? '',
    model: row.model ?? row.package?.model?.name ?? '',
    quantity: toSafeNumber(row.quantity ?? row.count),
    recievedCount: toSafeNumber(row.recievedCount),
    returnedCount: toSafeNumber(row.returnedCount),
});

interface PackageMeta {
    category: string;
    type: string;
    model: string;
    code: string;
}

const Counter: VFC = () => {
    // --- Filter state ---
    const { user } = useAuth();
    const storeId = (user as StoreUser)?.store;
    const [counterType, setCounterType] = useState<CounterType>(storeId ? 'store' : null);
    const [selectedPackageIds, setSelectedPackageIds] = useState<string[]>([]);
    const [selectedStoreIds, setSelectedStoreIds] = useState<string[]>([]);
    const [selectedSupplierIds, setSelectedSupplierIds] = useState<string[]>([]);
    const [selectedParcPalettIds, setSelectedParcPalettIds] = useState<string[]>([]);
    const [selectedWarehouseIds, setSelectedWarehouseIds] = useState<string[]>([]);
    const [validated, setValidated] = useState(false);

    // --- Inventory mode state ---
    const [isInventoryMode, setIsInventoryMode] = useState(false);
    const [inventoryValues, setInventoryValues] = useState<Record<string, number | undefined>>({});
    const [inventorySubmitting, setInventorySubmitting] = useState(false);

    // --- Data state ---
    const [packages, setPackages] = useState<DashboardRow[]>([]);
    const [parcPalettData, setParcPalettData] = useState<DashboardRow[]>([]);
    const [debtPackageData, setDebtPackageData] = useState<DashboardRow[]>([]);
    const [warehouseData, setWarehouseData] = useState<DashboardRow[]>([]);
    const [gridLoading, setGridLoading] = useState(false);
    const requestIdRef = useRef<number>(0);

    // --- Filter dropdown search state ---
    const [filteredStores, setFilteredStores] = useState<Store[]>([]);
    const [filteredSuppliers, setFilteredSuppliers] = useState<BaseModel[]>([]);

    const organizationId = user?.organization?.id;

    // --- Reference data hooks (for filter dropdowns only) ---
    const { data: packagingConfig } = usePackagingConfig(organizationId);
    const pkgList = packagingConfig?.packages ?? [];
    const { data: storesData, isLoading: storesLoading } = useStores();
    const supplierByStoreData = useSupplierByStore(storeId || '', organizationId);
    const supplierByOrgData = useSupplierByOrganization(organizationId || '');
    const supplierData = storeId ? supplierByStoreData.data : supplierByOrgData.data;
    const suppliersLoading = storeId ? supplierByStoreData.isLoading : supplierByOrgData.isLoading;
    const { data: palettesData, isLoading: palettesLoading } = usePalettes(organizationId || '');
    const { data: warehousesData, isLoading: warehousesLoading } = useWarehouses();

    const { data: inventoryConfig } = useInventoryConfig(organizationId);
    const isInventoryFeatureEnabled = isStoreInventoryEnabled(inventoryConfig, storeId);
    const inventoryDisabledReason = "L'inventaire est actuellement désactivé par l'administrateur.";
    const hasInventoryValues = Object.values(inventoryValues).some((v) => v !== undefined);

    const refDataLoading = storesLoading || suppliersLoading || palettesLoading || warehousesLoading;

    const packageMetaById = useMemo(() => {
        const map = new Map<string, PackageMeta>();
        pkgList.forEach((pkg) => {
            const orgArr = Array.isArray(pkg.organization)
                ? (pkg.organization as unknown as Array<{ id?: string; code?: string }>)
                : [];
            const orgEntry = orgArr.find((o) => String(o.id ?? '') === String(organizationId ?? ''));
            const code = orgEntry?.code || orgArr[0]?.code || '';
            map.set(pkg.id, {
                category: pkg.category?.name || '',
                type: pkg.type?.name || '',
                model: pkg.model?.name || '',
                code,
            });
        });
        return map;
    }, [pkgList, organizationId]);

    const packageIdByComposite = useMemo(() => {
        const map = new Map<string, string>();
        pkgList.forEach((pkg) => {
            const key = `${pkg.category?.name || ''}|${pkg.type?.name || ''}|${pkg.model?.name || ''}`;
            map.set(key, pkg.id);
        });
        return map;
    }, [pkgList]);

    const enrichDashboardRow = useCallback(
        (apiRow: DashboardApiRow): DashboardRow => {
            const normalized = normalizeDashboardRow(apiRow);
            const packageId = apiRow.package?._id || apiRow.package?.id;
            const packageMeta = packageId ? packageMetaById.get(packageId) : undefined;
            const rowCategoryFromPayload =
                typeof apiRow.package?.category === 'object' ? apiRow.package?.category?.name || '' : '';

            const resolvedCategory = normalized.category || rowCategoryFromPayload || packageMeta?.category || '';
            const resolvedType = normalized.type || packageMeta?.type || '';
            const resolvedModel = normalized.model || packageMeta?.model || '';
            const compositeKey = `${resolvedCategory}|${resolvedType}|${resolvedModel}`;
            const resolvedEntityId = normalized.entityId || packageId || packageIdByComposite.get(compositeKey) || '';

            return {
                ...normalized,
                entityId: resolvedEntityId,
                category: resolvedCategory,
                type: resolvedType,
                model: resolvedModel,
                code: normalized.code || packageMeta?.code || '',
            };
        },
        [packageMetaById, packageIdByComposite]
    );

    // Pre-select store for store-level users
    useEffect(() => {
        if (storeId && selectedStoreIds.length === 0) {
            setSelectedStoreIds([storeId]);
        }
    }, [storeId]);

    // --- Search handlers ---
    const handleStoreSearch = useCallback(
        (value: string) => {
            const filtered = value
                ? storesData?.filter(
                      (s) =>
                          s.name.toLowerCase().includes(value.toLowerCase()) ||
                          s.code.toLowerCase().includes(value.toLowerCase())
                  )
                : storesData;
            if (filtered) {
                const sorted = [...filtered].sort((a, b) => {
                    const numA = parseInt(a.code, 10);
                    const numB = parseInt(b.code, 10);
                    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
                    return a.code.localeCompare(b.code);
                });
                setFilteredStores(sorted);
            }
        },
        [storesData]
    );

    const handleSupplierSearch = useCallback(
        (value: string) => {
            const filtered = value
                ? supplierData?.filter((s) => s.name.toLowerCase().includes(value.toLowerCase()))
                : supplierData;
            if (filtered) setFilteredSuppliers(filtered);
        },
        [supplierData]
    );

    const debouncedStoreSearch = useMemo(() => debounce(handleStoreSearch, 300), [handleStoreSearch]);
    const debouncedSupplierSearch = useMemo(() => debounce(handleSupplierSearch, 300), [handleSupplierSearch]);

    // --- Counter type change ---
    const handleCounterTypeChange = (value: CounterType) => {
        setCounterType(value);
        setSelectedPackageIds([]);
        setSelectedStoreIds(storeId ? [storeId] : []);
        setSelectedSupplierIds([]);
        setSelectedParcPalettIds([]);
        setSelectedWarehouseIds([]);
        setValidated(false);
        setPackages([]);
        setParcPalettData([]);
        setDebtPackageData([]);
        setWarehouseData([]);
    };

    // --- CSV download ---
    const handleDownloadCsv = async () => {
        if (!counterType) return;

        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hour = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const timestamp = `${year}_${month}_${day}_${hour}_${minutes}`;

        // For the warehouse counter the CSV is built client-side from the table data
        // so that the same filters (warehouse, packaging type) are always respected.
        if (counterType === 'warehouse') {
            const header = 'Compteur;Entité;Catégorie;Type;Code;Modèle;Quantité';
            const csvRows = [header];
            for (const row of warehouseData) {
                const qty = toSafeNumber(row.quantity);
                const qtyStr = qty >= 0 ? String(qty) : String(qty); // keep negative sign as-is
                csvRows.push(
                    ['Compteur entrepôt', row.entity, row.category, row.type, row.code, row.model, qtyStr]
                        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
                        .join(';')
                );
            }
            const bom = '\uFEFF';
            const blob = new Blob([bom + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `export_compteur_entrepot_${timestamp}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            return;
        }

        const storeIdsToFetch = selectedStoreIds.length > 0 ? selectedStoreIds : storeId ? [storeId] : [];

        const response = await apiClient().get('/dashboardCounters', {
            params: {
                storeIds: storeIdsToFetch.join(','),
                supplierIds: selectedSupplierIds.join(','),
                packageIds: selectedPackageIds.join(','),
                parcPalettIds: selectedParcPalettIds.join(','),
                organizationId: organizationId || '',
                counterType,
                format: 'csv',
            },
            responseType: 'blob',
        });

        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');

        let filename: string;
        if (counterType === 'store') {
            filename = `export_compteur_magasins_${timestamp}.csv`;
        } else if (counterType === 'debt') {
            filename = `export_compteur_dettefournisseur_${timestamp}.csv`;
        } else if (counterType === 'parc') {
            filename = `export_compteur_parcpalettes_${timestamp}.csv`;
        } else {
            filename = `export_compteur_magasins_${timestamp}.csv`;
        }
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };

    // --- Validate / fetch ---
    const handleValidate = async () => {
        if (!counterType) return;
        setValidated(true);
        setGridLoading(true);

        requestIdRef.current += 1;
        const requestId = requestIdRef.current;

        try {
            const storeIdsToFetch = selectedStoreIds.length > 0 ? selectedStoreIds : storeId ? [storeId] : [];

            // Warehouse counter uses /warehouseCounters directly (same as dashboard)
            // so that dynamically-created entries (e.g. created by afterFlowCreation hooks)
            // are included — /dashboardCounters only returns pre-seeded documents.
            if (counterType === 'warehouse') {
                const response = await apiClient().get('/warehouseCounters', {
                    params: {
                        organizationId: organizationId || '',
                        warehouseIds: selectedWarehouseIds.join(','),
                        packageIds: selectedPackageIds.join(','),
                    },
                });

                if (requestId !== requestIdRef.current) return;

                // Build two lookup maps from the API response so we can match by
                // warehouse+packageId (precise) or warehouse+type+model (fallback).
                const counterByWarehousePkg = new Map<string, number>();
                const counterByWarehouseTypeModel = new Map<string, number>();
                // Map packageId → code (filtered by current organization).
                // The /warehouseCounters response embeds full orgInternCodes; the
                // packagingConfig `pkgList` used below sometimes omits this field,
                // which is why the Code column previously came back empty.
                const codeByPackageId = new Map<string, string>();
                (response.data ?? []).forEach((r: any) => {
                    const whId = String(r.warehouse?._id ?? r.warehouse?.id ?? '');
                    const pkgId = String(r.package?._id ?? r.package?.id ?? '');
                    const qty = toSafeNumber(r.count ?? r.quantity ?? 0);
                    if (whId && pkgId) counterByWarehousePkg.set(`${whId}|${pkgId}`, qty);
                    const type = String(r.package?.type?.name ?? r.type ?? '');
                    const model = String(r.package?.model?.name ?? r.model ?? '');
                    if (whId && type && model) counterByWarehouseTypeModel.set(`${whId}|${type}|${model}`, qty);

                    // Resolve org-specific code from orgInternCodes
                    if (pkgId && !codeByPackageId.has(pkgId)) {
                        const codes: Array<{ code?: string; organization?: string }> = r.package?.orgInternCodes ?? [];
                        const orgCode = codes.find((c) => String(c.organization ?? '') === String(organizationId));
                        const resolvedCode = orgCode?.code ?? codes[0]?.code ?? '';
                        if (resolvedCode) codeByPackageId.set(pkgId, resolvedCode);
                    }
                });

                // Determine which warehouses to display
                const allWarehouses = warehousesData ?? [];
                const warehousesToShow =
                    selectedWarehouseIds.length > 0
                        ? allWarehouses.filter((w) => selectedWarehouseIds.includes(w.id))
                        : allWarehouses;

                // Determine which packages to display (respect optional packaging-type filter)
                const packagesToShow =
                    selectedPackageIds.length > 0
                        ? pkgList.filter((pkg) => selectedPackageIds.includes(pkg.id))
                        : pkgList;

                // Produce one row per warehouse × package — always show all types, even at 0
                const rows: DashboardRow[] = [];
                for (const wh of warehousesToShow) {
                    for (const pkg of packagesToShow) {
                        const pkgKey = `${wh.id}|${pkg.id}`;
                        const typeKey = `${wh.id}|${pkg.type?.name ?? ''}|${pkg.model?.name ?? ''}`;
                        const quantity =
                            counterByWarehousePkg.get(pkgKey) ?? counterByWarehouseTypeModel.get(typeKey) ?? 0;

                        // Prefer code from API response (already org-filtered), then
                        // packagingConfig, then any orgInternCodes on pkg itself.
                        const resolvedCode =
                            codeByPackageId.get(pkg.id) ??
                            packageMetaById.get(pkg.id)?.code ??
                            pkg.orgInternCodes?.[0]?.code ??
                            '';

                        rows.push({
                            counter: '',
                            entity: wh.name,
                            entityId: wh.id,
                            category: pkg.category?.name ?? '',
                            type: pkg.type?.name ?? '',
                            code: resolvedCode,
                            model: pkg.model?.name ?? '',
                            quantity,
                        });
                    }
                }

                setWarehouseData(rows);
                setPackages([]);
                setDebtPackageData([]);
                setParcPalettData([]);
                return;
            }

            const counterMap: Record<string, string> = {
                store: 'packages',
                debt: 'debts',
                parc: 'parcsPalette',
            };

            const response = await apiClient().get('/dashboardCounters', {
                params: {
                    storeIds: storeIdsToFetch.join(','),
                    supplierIds: selectedSupplierIds.join(','),
                    packageIds: selectedPackageIds.join(','),
                    parcPalettIds: selectedParcPalettIds.join(','),
                    warehouseIds: selectedWarehouseIds.join(','),
                    organizationId: organizationId || '',
                    counters: counterMap[counterType],
                },
            });

            if (requestId !== requestIdRef.current) return;

            if (counterType === 'store') {
                setPackages((response.data.packages ?? []).map(enrichDashboardRow));
                setDebtPackageData([]);
                setParcPalettData([]);
                setWarehouseData([]);
            } else if (counterType === 'debt') {
                setDebtPackageData((response.data.debts?.debts ?? []).map(enrichDashboardRow));
                setPackages([]);
                setParcPalettData([]);
                setWarehouseData([]);
            } else if (counterType === 'parc') {
                setParcPalettData((response.data.parcsPalette ?? []).map(enrichDashboardRow));
                setPackages([]);
                setDebtPackageData([]);
                setWarehouseData([]);
            }
        } catch (err) {
            if (requestId !== requestIdRef.current) return;
            setPackages([]);
            setDebtPackageData([]);
            setParcPalettData([]);
        } finally {
            setGridLoading(false);
        }
    };

    // --- Format helpers (sign already handled by API) ---

    const formatQuantity = (value: number) => {
        if (!storeId && !organizationId) return '';
        const safeValue = toSafeNumber(value);
        return safeValue >= 0 ? safeValue : `- ${Math.abs(safeValue)}`;
    };

    // --- Unified table ---

    const rawData: DashboardRow[] =
        counterType === 'store'
            ? packages
            : counterType === 'debt'
            ? debtPackageData
            : counterType === 'parc'
            ? parcPalettData
            : counterType === 'warehouse'
            ? warehouseData
            : [];

    const rentalPackageIds = useMemo(() => {
        const ids = new Set<string>();
        pkgList.forEach((pkg) => {
            if (pkg.rental) {
                if (pkg.id) ids.add(pkg.id);
                const mongoId = (pkg as { _id?: string })._id;
                if (mongoId) ids.add(mongoId);
            }
        });
        return ids;
    }, [pkgList]);

    const rentalPackageComposites = useMemo(() => {
        const set = new Set<string>();
        pkgList.forEach((pkg) => {
            if (pkg.rental) {
                set.add(`${pkg.category?.name || ''}|${pkg.type?.name || ''}|${pkg.model?.name || ''}`);
            }
        });
        return set;
    }, [pkgList]);

    const isRowRental = useCallback(
        (row: { entityId: string; category: string; type: string; model: string }) => {
            if (row.entityId && rentalPackageIds.has(row.entityId)) return true;
            const composite = `${row.category || ''}|${row.type || ''}|${row.model || ''}`;
            return rentalPackageComposites.has(composite);
        },
        [rentalPackageIds, rentalPackageComposites]
    );

    const tableData = useMemo(() => {
        let rows = (rawData || []).map((row, idx) => ({
            key: `${row.entityId || idx}-${idx}`,
            entityId: row.entityId || '',
            entity: row.entity || '',
            category: row.category || '',
            type: row.type || '',
            code: row.code || '',
            model: row.model || '',
            quantity: toSafeNumber(row.quantity),
        }));

        // For "Dette transporteur": only show packages where rental = false
        if (counterType === 'debt') {
            rows = rows.filter((r) => !isRowRental(r));
        }

        return rows.sort((a, b) =>
            (a.code || '').localeCompare(b.code || '', undefined, { numeric: true, sensitivity: 'base' })
        );
    }, [rawData, counterType, pkgList, organizationId, isRowRental]);

    const entityColumnTitle =
        counterType === 'store'
            ? 'Magasin'
            : counterType === 'debt'
            ? 'Transporteur'
            : counterType === 'warehouse'
            ? 'Entrepôt'
            : 'Parc Palette';

    const quantityCellClass =
        counterType === 'store'
            ? 'count-cell'
            : counterType === 'debt'
            ? 'debt-cell'
            : counterType === 'warehouse'
            ? 'warehouse-cell'
            : 'parc-cell';

    const tableColumns = useMemo(() => {
        const cols: ColumnsType<TableRow> = [
            // Only show entity column if not a store user
            ...(!storeId ? [{ title: entityColumnTitle, dataIndex: 'entity', key: 'entity' }] : []),
            { title: 'Catégorie', dataIndex: 'category', key: 'category' },
            { title: 'Type', dataIndex: 'type', key: 'type' },
            { title: 'Code', dataIndex: 'code', key: 'code' },
            { title: 'Modèle', dataIndex: 'model', key: 'model' },
            {
                title: 'Quantité',
                dataIndex: 'quantity',
                key: 'quantity',
                render: (value: number) => <span className={quantityCellClass}>{formatQuantity(value)}</span>,
            },
        ];

        if (isInventoryMode && counterType === 'store') {
            cols.push({
                title: 'Quantité Inventaire',
                key: 'quantiteInventaire',
                render: (_: unknown, record: TableRow) => (
                    <InputNumber
                        value={inventoryValues[record.key]}
                        onChange={(val) => handleInventoryValueChange(record.key, val)}
                        placeholder="Saisir"
                        onClick={(e) => e.stopPropagation()}
                        style={{ width: '100%' }}
                    />
                ),
            });
            cols.push({
                title: 'Écart Constaté',
                key: 'ecartConstate',
                render: (_: unknown, record: TableRow) => {
                    const diff = getInventoryDifference(record.key, record.quantity);
                    return <span className={quantityCellClass}>{diff !== undefined ? diff : '-'}</span>;
                },
            });
        }

        return cols;
    }, [entityColumnTitle, quantityCellClass, formatQuantity, storeId, isInventoryMode, inventoryValues, counterType]);

    useEffect(() => {
        if (process.env.NODE_ENV === 'production') return;
        if (!counterType) return;

        console.groupCollapsed(`[Counter] ${counterType} grid rows`);
        console.table(tableData);
        console.groupEnd();
    }, [counterType, tableData]);

    // If the admin disables inventory while the store user is in inventory mode, exit it.
    useEffect(() => {
        if (!isInventoryFeatureEnabled && isInventoryMode) {
            setIsInventoryMode(false);
            setInventoryValues({});
        }
    }, [isInventoryFeatureEnabled, isInventoryMode]);

    // --- Render ---

    // --- Inventory mode helpers ---
    const handleInventoryValueChange = (rowKey: string, value: number | null) => {
        setInventoryValues((prev) => ({
            ...prev,
            [rowKey]: value !== null ? value : undefined,
        }));
    };

    const getInventoryDifference = (rowKey: string, quantity: number): number | undefined => {
        const val = inventoryValues[rowKey];
        if (val === undefined) return undefined;
        return val - quantity;
    };

    const getInventoryRowClass = (rowKey: string, quantity: number): string => {
        const diff = getInventoryDifference(rowKey, quantity);
        if (diff === undefined) return '';
        if (diff > 0) return 'inventory-row-green';
        if (diff < 0) return 'inventory-row-red';
        return 'inventory-row-gray';
    };

    const handleValidateInventory = async () => {
        if (!isInventoryFeatureEnabled) {
            message.warning(inventoryDisabledReason);
            return;
        }
        const inventoryItems = tableData
            .filter((row) => inventoryValues[row.key] !== undefined)
            .map((row) => ({
                package: row.entityId,
                countedValue: inventoryValues[row.key]!,
                actualDifference: inventoryValues[row.key]! - row.quantity,
            }));

        if (inventoryItems.length === 0) {
            message.warning('Veuillez remplir au moins une quantité inventaire.');
            return;
        }
        try {
            setInventorySubmitting(true);
            await createStoreInventory({
                organizationId: organizationId || '',
                storeId: storeId || '',
                packagesInventory: inventoryItems,
            });
            message.success('Inventaire validé avec succès.');
            setIsInventoryMode(false);
            setInventoryValues({});
            await handleValidate();
        } catch (err: any) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.message || err?.response?.data || err?.message || String(err);
            const detailStr = typeof detail === 'string' ? detail : JSON.stringify(detail);
            message.error(`Erreur ${String(status ?? '')}: ${detailStr}`);
        } finally {
            setInventorySubmitting(false);
        }
    };

    return (
        <div className="counter-page">
            <h1>Compteurs</h1>

            <div className="counter-filters">
                <Form layout="vertical">
                    {!storeId && (
                        <Row gutter={16}>
                            <Col xs={24} sm={24} md={8} lg={8}>
                                <Form.Item label="Type de compteur" className="bold-label">
                                    <Select
                                        placeholder="Sélectionner un type de compteur"
                                        value={counterType}
                                        onChange={handleCounterTypeChange}
                                        allowClear
                                    >
                                        <Option value="store">Compteur magasin</Option>
                                        <Option value="debt">Dette transporteur</Option>
                                        <Option value="parc">Compteur Parc Palette</Option>
                                        <Option value="warehouse">Compteur Entrepôt</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    )}

                    {counterType && (
                        <>
                            <Row gutter={16}>
                                {/* Type d'emballage — always shown when counterType is set */}
                                <Col xs={24} sm={24} md={12} lg={12}>
                                    <Form.Item label="Type d'emballage" className="bold-label">
                                        <Select
                                            mode="multiple"
                                            showSearch
                                            placeholder="Sélectionner un emballage"
                                            notFoundContent="Aucun résultat"
                                            filterOption={(input, option) =>
                                                option?.children
                                                    ? option.children
                                                          .toString()
                                                          .toLowerCase()
                                                          .includes(input.toLowerCase())
                                                    : false
                                            }
                                            onChange={(value: string[]) => setSelectedPackageIds(value)}
                                            value={selectedPackageIds}
                                        >
                                            {pkgList.map((pkg) => (
                                                <Option key={pkg.id} value={pkg.id}>
                                                    {pkg.type.name === pkg.model.name
                                                        ? pkg.type.name
                                                        : `${pkg.type.name} + ${pkg.model.name}`}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>

                                {/* Magasin — only for store counter */}
                                {counterType === 'store' && (
                                    <Col xs={24} sm={24} md={12} lg={12}>
                                        <Form.Item label="Magasin" className="bold-label">
                                            <Select
                                                mode="multiple"
                                                showSearch
                                                placeholder="Rechercher un magasin"
                                                onSearch={debouncedStoreSearch}
                                                filterOption={false}
                                                maxTagCount="responsive"
                                                suffixIcon={<SearchOutlined />}
                                                notFoundContent="Aucun résultat"
                                                onChange={(
                                                    value: Array<{ key: string; value: string; label: string }>
                                                ) => setSelectedStoreIds(value.map((v) => v.value))}
                                                value={selectedStoreIds.map((id) => {
                                                    let store = filteredStores.find((s) => s.id === id);
                                                    if (!store && storesData) {
                                                        store = storesData.find((s) => s.id === id);
                                                    }
                                                    return store
                                                        ? {
                                                              key: store.id,
                                                              value: store.id,
                                                              label: `${store.code} - ${store.name}`,
                                                          }
                                                        : { key: id, value: id, label: id };
                                                })}
                                                labelInValue
                                                disabled={!!storeId}
                                                onDropdownVisibleChange={(open) => {
                                                    if (open) handleStoreSearch('');
                                                }}
                                            >
                                                {filteredStores.map((store) => (
                                                    <Option key={store.id} value={store.id}>
                                                        {store.code} - {store.name}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                )}

                                {/* Transporteur — only for debt counter */}
                                {counterType === 'debt' && (
                                    <Col xs={24} sm={24} md={12} lg={12}>
                                        <Form.Item label="Transporteur" className="bold-label">
                                            <Select
                                                mode="multiple"
                                                showSearch
                                                placeholder="Rechercher un Transporteur"
                                                onSearch={debouncedSupplierSearch}
                                                filterOption={false}
                                                maxTagCount="responsive"
                                                suffixIcon={<SearchOutlined />}
                                                notFoundContent="Aucun résultat"
                                                onChange={(value: any[]) =>
                                                    setSelectedSupplierIds(value.map((v) => v.value))
                                                }
                                                value={selectedSupplierIds.map((id) => {
                                                    let supplier = filteredSuppliers.find((s) => s.id === id);
                                                    if (!supplier && supplierData) {
                                                        supplier = supplierData.find((s: any) => s.id === id);
                                                    }
                                                    return supplier
                                                        ? {
                                                              key: supplier.id,
                                                              value: supplier.id,
                                                              label: supplier.name,
                                                          }
                                                        : { key: id, value: id, label: id };
                                                })}
                                                labelInValue
                                                onDropdownVisibleChange={(open) => {
                                                    if (open) handleSupplierSearch('');
                                                }}
                                            >
                                                {filteredSuppliers.map((supplier) => (
                                                    <Option key={supplier.id} value={supplier.id}>
                                                        {supplier.name}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                )}

                                {/* Entrepôt — only for warehouse counter */}
                                {counterType === 'warehouse' && (
                                    <Col xs={24} sm={24} md={12} lg={12}>
                                        <Form.Item label="Entrepôt" className="bold-label">
                                            <Select
                                                mode="multiple"
                                                showSearch
                                                placeholder="Sélectionner un entrepôt"
                                                filterOption={(input, option) =>
                                                    option?.children
                                                        ? option.children
                                                              .toString()
                                                              .toLowerCase()
                                                              .includes(input.toLowerCase())
                                                        : false
                                                }
                                                maxTagCount="responsive"
                                                notFoundContent="Aucun résultat"
                                                onChange={(value: string[]) => setSelectedWarehouseIds(value)}
                                                value={selectedWarehouseIds}
                                            >
                                                {(warehousesData ?? []).map((wh) => (
                                                    <Option key={wh.id} value={wh.id}>
                                                        {wh.name}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                )}

                                {/* Parc palette — only for parc counter */}
                                {counterType === 'parc' && (
                                    <Col xs={24} sm={24} md={12} lg={12}>
                                        <Form.Item label="Parc palette" className="bold-label">
                                            <Select
                                                mode="multiple"
                                                showSearch
                                                placeholder="Parc palette"
                                                filterOption={(input, option) =>
                                                    option?.children
                                                        ? option.children
                                                              .toString()
                                                              .toLowerCase()
                                                              .includes(input.toLowerCase())
                                                        : false
                                                }
                                                maxTagCount="responsive"
                                                suffixIcon={<SearchOutlined />}
                                                notFoundContent="Aucun résultat"
                                                onChange={(value: any[]) =>
                                                    setSelectedParcPalettIds(value.map((v) => v.value))
                                                }
                                                value={selectedParcPalettIds.map((id) => {
                                                    const palette = palettesData?.find((s) => s.id === id);
                                                    return palette
                                                        ? {
                                                              key: palette.id,
                                                              value: palette.id,
                                                              label: palette.name,
                                                          }
                                                        : { key: id, value: id, label: id };
                                                })}
                                                labelInValue
                                            >
                                                {(palettesData ?? []).map((palette) => (
                                                    <Option key={palette.id} value={palette.id}>
                                                        {palette.name}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                )}
                            </Row>

                            <Row style={{ alignItems: 'center' }}>
                                <Col>
                                    <Button
                                        type="primary"
                                        onClick={handleValidate}
                                        loading={gridLoading}
                                        disabled={refDataLoading}
                                    >
                                        Valider
                                    </Button>
                                    <Button
                                        onClick={handleDownloadCsv}
                                        disabled={!counterType || refDataLoading}
                                        style={{ marginLeft: 8 }}
                                    >
                                        Télécharger CSV
                                    </Button>
                                </Col>
                                {storeId && counterType === 'store' && (
                                    <Col style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span className={!isInventoryMode ? 'active-mode-label' : ''}>
                                            Mode Visualisation
                                        </span>
                                        <Tooltip title={!isInventoryFeatureEnabled ? inventoryDisabledReason : ''}>
                                            <Switch
                                                checked={isInventoryMode}
                                                disabled={!isInventoryFeatureEnabled}
                                                onChange={(checked) => {
                                                    setIsInventoryMode(checked);
                                                    if (!checked) setInventoryValues({});
                                                }}
                                            />
                                        </Tooltip>
                                        <span className={isInventoryMode ? 'active-mode-label' : ''}>
                                            Mode Inventaire
                                        </span>
                                    </Col>
                                )}
                            </Row>
                        </>
                    )}
                </Form>
            </div>

            {/* Results */}
            {validated && gridLoading && (
                <div className="package-count-grid">
                    <Spin />
                </div>
            )}

            {validated && !gridLoading && (
                <div className="package-count-grid">
                    {tableData.length > 0 ? (
                        <div className="grid-section">
                            <div className="grid-section-header">
                                {counterType === 'store' && <ShopOutlined className="grid-section-icon" />}
                                {counterType === 'debt' && <TruckOutlined className="grid-section-icon" />}
                                {counterType === 'parc' && <PalettCount className="grid-section-icon" />}
                                <h3>
                                    {counterType === 'store' && 'Détails Compteur Magasins'}
                                    {counterType === 'debt' && 'Détails Dette Transporteur'}
                                    {counterType === 'parc' && 'Détails Parc Palettes'}
                                    {counterType === 'warehouse' && 'Détails Compteur Entrepôt'}
                                </h3>
                            </div>
                            <Table
                                columns={tableColumns}
                                dataSource={tableData}
                                pagination={false}
                                size="middle"
                                bordered
                                rowClassName={(record) =>
                                    isInventoryMode && counterType === 'store'
                                        ? getInventoryRowClass(record.key, record.quantity)
                                        : ''
                                }
                            />
                            {isInventoryMode && counterType === 'store' && (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
                                    <Tooltip title={!isInventoryFeatureEnabled ? inventoryDisabledReason : ''}>
                                        <Button
                                            type="primary"
                                            size="large"
                                            loading={inventorySubmitting}
                                            disabled={!isInventoryFeatureEnabled || !hasInventoryValues}
                                            onClick={handleValidateInventory}
                                        >
                                            Valider l&apos;inventaire
                                        </Button>
                                    </Tooltip>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="grid-section">
                            <p style={{ textAlign: 'center', color: '#999', padding: 24 }}>Aucune donnée trouvée</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Counter;
